import { ClusterServiceType, TolerationEffect, TolerationOperator } from '@qdrant/qdrant-cloud-public-api/cluster/v1';
import { TopologySpreadConstraintWhenUnsatisfiable } from '@qdrant/qdrant-cloud-public-api/common/v1';
import { z } from 'zod';
import { preprocessEmptyStringTo, validators } from '@/utils/form-utils';

export const DEFAULT_KUBERNETES_SERVICE_TYPE: ClusterServiceType = ClusterServiceType.CLUSTER_IP;
export const serviceTypeOptions = [
  { value: ClusterServiceType.CLUSTER_IP, label: 'ClusterIP' },
  { value: ClusterServiceType.NODE_PORT, label: 'NodePort' },
  { value: ClusterServiceType.LOAD_BALANCER, label: 'LoadBalancer' },
] as const;

export const MAX_KEY_VALUE_PAIRS = 64;
export const MAX_KEY_LENGTH = 63 + 253; // = 316 ->  253 (optional prefix) + 63 (mandatory name);
export const MAX_VALUE_LENGTH = 65536;
export const keyValuePairArrayValidator = z
  .array(
    z.object({
      // See https://kubernetes.io/docs/concepts/overview/working-with-objects/annotations/#syntax-and-character-set
      key: validators.kubernetesLabelKey,
      value: z
        .string()
        .min(1, 'Value is required')
        .max(MAX_VALUE_LENGTH, `Value must be at most ${MAX_VALUE_LENGTH} characters`),
    }),
  )
  .max(MAX_KEY_VALUE_PAIRS, `Maximum number of key/value pairs is ${MAX_KEY_VALUE_PAIRS}`)
  .superRefine((items, ctx) => {
    const uniqueValues = new Map<string, number>();
    for (const [idx, { key }] of items.entries()) {
      const firstAppearanceIndex = uniqueValues.get(key);
      if (firstAppearanceIndex !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: `Key must be unique`,
          path: [idx, 'key'],
        });
        ctx.addIssue({
          code: 'custom',
          message: `Key must be unique`,
          path: [firstAppearanceIndex, 'key'],
        });
      } else {
        uniqueValues.set(key, idx);
      }
    }
  })
  .optional();

export type KeyValuePair = NonNullable<z.infer<typeof keyValuePairArrayValidator>>[number];

const zodCoercedPositiveInt = preprocessEmptyStringTo(
  z.coerce
    .bigint<bigint>({ error: 'Input a positive number without decimals' })
    .positive({ error: 'Input a positive number without decimals' })
    .optional(),
);
export const tolerationsValidator = z
  .array(
    z
      .object({
        operator: preprocessEmptyStringTo(z.enum(TolerationOperator, { error: 'Operator is invalid' }).optional()),
        key: validators.optionalKubernetesLabelKey,
        effect: preprocessEmptyStringTo(z.enum(TolerationEffect, { error: 'Effect is invalid' }).optional()),
        value: validators.optionalKubernetesQualifiedName,
        tolerationSeconds: zodCoercedPositiveInt,
      })
      // Make sure that the Value is always kept as undefined when the operator is `Exists` and the value is an empty string.
      .transform((val) =>
        val.operator === TolerationOperator.EXISTS && val.value === '' ? { ...val, value: undefined } : val,
      ),
  )
  .superRefine((items, ctx) => {
    for (const [idx, { key, operator, value, effect, tolerationSeconds }] of items.entries()) {
      // If operator is not set, it is treated as `Equal` (kubernetes default).
      // If operator is `Equal`, value must be defined (empty string is valid).
      if ((!operator || operator === TolerationOperator.EQUAL) && value == null) {
        ctx.addIssue({
          code: 'custom',
          message: 'Value is required',
          path: [idx, 'value'],
        });
      } else if (operator === TolerationOperator.EXISTS && value) {
        ctx.addIssue({
          code: 'custom',
          message: 'Value must be empty',
          path: [idx, 'value'],
        });
      }

      if (operator !== TolerationOperator.EXISTS && !key) {
        ctx.addIssue({
          code: 'custom',
          message: 'Key must be defined if operator is not `Exists`',
          path: [idx, 'key'],
        });
      }

      if (tolerationSeconds && effect !== TolerationEffect.NO_EXECUTE) {
        ctx.addIssue({
          code: 'custom',
          message: "Effect must be 'NoExecute' when `tolerationSeconds` is set",
          path: [idx, 'effect'],
        });
      }
    }
  })
  .optional();

export type TolerationFormField = z.infer<typeof tolerationsValidator>;

export const topologySpreadConstraintsValidator = z
  .array(
    z.object({
      // `.optional()` wraps the preprocess rather than the number: an absent `maxSkew` is accepted,
      // but the empty string a cleared field yields becomes `undefined` *inside* a required number
      // and fails. Moving it inward would silently accept a cleared field.
      maxSkew: preprocessEmptyStringTo(
        z.coerce
          .number<number>({ error: 'Max Skew must be a positive number' })
          .positive({ error: 'Max Skew must be a positive number' }),
      ).optional(),
      topologyKey: z.string().min(1, 'Topology Key is required'),
      whenUnsatisfiable: preprocessEmptyStringTo(
        z
          .enum(TopologySpreadConstraintWhenUnsatisfiable, {
            error: "When Unsatisfiable must be 'Schedule Anyway' or 'Do Not Schedule'",
          })
          .optional(),
      ),
    }),
  )
  .optional();

export type TopologySpreadConstraintFormField = z.infer<typeof topologySpreadConstraintsValidator>;
export const getKeyValuePairsArray = (data?: Record<string, string> | null) =>
  data ? Object.entries(data).map(([key, value]) => ({ key, value })) : [];

export const mapValueKeyPairArrayToObj = (
  data?: { key?: string; value?: string }[],
  isValid?: boolean,
): Record<string, string> | undefined => {
  /*
      Map should return an empty object (`{}`) if the form is invalid or there is no key value.
      If set to undefined, the field will not be sent as part of the request when clearing the value,
      which is not what we want. And, for the invalid form case, submission should be prevented by
      the validation anyways.
    */
  if (!isValid || !data?.length) {
    return {};
  }

  return data.reduce<Record<string, string>>((acc, { key, value }) => {
    if (key && value) {
      acc[key] = value;
    }
    return acc;
  }, {});
};
